import React, { useState, useMemo, useCallback } from 'react'
import ModalFreemium from 'components/ModalFreemium'
import Direcao from 'assets/logos/direcao-square.png'
import { WrapperContainer } from 'pages/Course/components/ModuleAcordeon/styles'

interface CourseData {
  _id: string
  name: string
  icon?: string
  discount?: number
  permanentLink?: string
  totalCourse?: {
    videos?: { amount: number }
    excertos?: { amount: number }
  }
  total?: {
    videos?: { amount: number }
    excertos?: { amount: number }
  }
  isFreemium?: boolean
  isSubscription?: boolean
  children?: Array<{
    ref: string
    _id?: string
    name: string
    discount?: number
    permanentLink?: string
    total?: {
      video?: { amount: number }
      excerto?: { amount: number }
    }
  }>
}

interface ModuleData {
  _id: string
  ref?: string
  name: string
  discount?: number
  permanentLink?: string
  total?: {
    video?: { amount: number }
    excerto?: { amount: number }
  }
}

interface UseFreemiumModalProps {
  course: CourseData | null | undefined
  moduleId?: string
  module?: ModuleData | null
}

interface UseFreemiumModalReturn {
  isOpen: boolean
  openModal: () => void
  closeModal: () => void
  renderModal: () => React.ReactElement | null
  hasRequiredData: boolean
}

/**
 * Hook compartilhado para gerenciar o modal do freemium
 * Prepara os dados do curso e módulo no formato esperado pelo ModalFreemium
 */
export const useFreemiumModal = ({
  course,
  moduleId,
  module,
}: UseFreemiumModalProps): UseFreemiumModalReturn => {
  const [isOpen, setIsOpen] = useState(false)

  // Encontrar o módulo nos children do course se não foi fornecido diretamente
  const moduleData = useMemo(() => {
    if (module) return module

    if (!course?.children || !moduleId) return null

    const foundModule = course.children.find((m) => m.ref === moduleId || m._id === moduleId)

    if (!foundModule) return null

    return {
      _id: foundModule._id || foundModule.ref,
      ref: foundModule.ref,
      name: foundModule.name,
      discount: foundModule.discount,
      permanentLink: foundModule.permanentLink,
      total: foundModule.total,
    }
  }, [course, moduleId, module])

  // Preparar dados do módulo no formato esperado pelo ModalFreemium
  const moduleInfo = useMemo(() => {
    if (!moduleData) return null

    return {
      _id: moduleData._id,
      name: moduleData.name,
      icon: course?.icon ?? Direcao,
      discount: moduleData.discount ?? 0,
      permanentLink: moduleData.permanentLink ?? '',
      qtd_videos: moduleData.total?.video?.amount ?? 0,
      qtd_pdf: moduleData.total?.excerto?.amount ?? 0,
    }
  }, [moduleData, course?.icon])

  // Preparar dados do curso no formato esperado pelo ModalFreemium
  const courseInfo = useMemo(() => {
    if (!course) return null

    const totalCourse = course.totalCourse || course.total

    return {
      _id: course._id,
      name: course.name,
      icon: course.icon ?? Direcao,
      discount: course.discount ?? 0,
      permanentLink: course.permanentLink ?? '',
      qtd_videos: totalCourse?.videos?.amount ?? totalCourse?.excertos?.amount ?? 0,
      qtd_pdf: totalCourse?.excertos?.amount ?? 0,
    }
  }, [course])

  // Função para renderizar o modal
  const renderModal = useCallback(() => {
    if (!isOpen || !moduleInfo || !courseInfo) return null

    return (
      <WrapperContainer>
        <ModalFreemium
          module={moduleInfo}
          course={courseInfo}
          setOpenModal={setIsOpen}
          subscription={course?.isFreemium && course?.isSubscription}
        />
      </WrapperContainer>
    )
  }, [isOpen, moduleInfo, courseInfo, course])

  const openModal = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  return {
    isOpen,
    openModal,
    closeModal,
    renderModal,
    hasRequiredData: !!moduleInfo && !!courseInfo,
  }
}
